import React, { useRef } from "react";
import { uploadSingleFileAction } from "@/store/actions/imageupload.actions";
import UploadButton from "@/components/common/UploadButton";
import { getAsset } from "@team-shiphouse/shiphouse-sdk";
import { generateImageSrc } from "@/utils/helper";
import { Product } from "@/types/models/product";
import { CategoryAsset } from "@/types/models/category";

// Common interface for assets that can be displayed
interface DisplayableAsset {
  id?: string;
  asset_id: string;
  asset?: {
    id: string;
    path: string;
    original_name: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface ImageUploadProps {
  images?: string[]; // Asset IDs (deprecated - use assets instead)
  assets?: Product["assets"] | CategoryAsset[]; // Asset objects for preview
  onChange?: (file: File | null, id?: number) => void;
  onUploadComplete?: (filename: string) => void;
  type: "single" | "multiple";
  storeId?: string;
  path?: string;
  useNewApi?: boolean;
  onOpenFileManager?: () => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  images,
  assets,
  onChange,
  onUploadComplete,
  type,
  storeId,
  path = "/store/products/images",
  useNewApi = false,
  onOpenFileManager,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      for (const file of Array.from(files)) {
        if (file.type.startsWith("image/")) {
          if (useNewApi && storeId) {
            try {
              const result = await uploadSingleFileAction(file, storeId, path);
              if (result.success) {
                onUploadComplete?.(result.uploadedFileName);
              }
            } catch (error) {
              console.error("Upload failed:", error);
            }
          } else {
            onChange?.(file);
          }
        }
      }
    }
  };

  const handleRemove = (index: number) => {
    onChange?.(null, index);
  };

  // Prefer assets over images for rendering
  const displayAssets = (assets || []) as DisplayableAsset[];

  return (
    <div className="flex gap-4 items-center">
      {displayAssets.map((assetItem, idx) => {
        // Safely access asset path
        const assetPath = assetItem.asset?.path || "";
        const imageSrc = generateImageSrc(assetPath, 100, 128);
        console.log("🖼️ Rendering image preview from asset:", { 
          assetId: assetItem.asset_id, 
          path: assetPath, 
          generated: imageSrc 
        });

        return (
          <div
            key={assetItem.id || idx}
            className="relative w-24 h-32 rounded-xl overflow-hidden bg-gray-800 flex items-center justify-center"
          >
            <img
              src={imageSrc}
              alt={assetItem.asset?.original_name || `Preview ${idx + 1}`}
              className="object-cover w-full h-full"
              onError={(e) => {
                console.error("❌ Image failed to load:", { 
                  assetId: assetItem.asset_id,
                  path: assetPath, 
                  src: imageSrc 
                });
                // Hide image on error to prevent 404 spam
                e.currentTarget.style.display = "none";
                // Show a placeholder background color instead
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  parent.style.backgroundColor = "#374151"; // gray-700
                  parent.innerHTML = '<div class="flex items-center justify-center w-full h-full text-gray-400 text-xs">Image unavailable</div>';
                }
              }}
            />
            <button
              type="button"
              onClick={() => handleRemove(idx)}
              className="absolute top-12 left-12 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-red-600 -translate-x-[50%] cursor-pointer"
              aria-label="Remove image"
            >
              &#10005;
            </button>
          </div>
        );
      })}
      {/* Always show the upload box at the end */}
      {(type === "multiple" || displayAssets.length < 1) && (
        <>
          {!onOpenFileManager && (
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.gif,.bmp,.webp"
              className="hidden"
              multiple
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          )}
          <UploadButton
            title="Upload Image"
            onClick={
              onOpenFileManager
                ? onOpenFileManager
                : () => fileInputRef.current?.click()
            }
          />
        </>
      )}
    </div>
  );
};

export default ImageUpload;
